/* © NXAPP Limited */
import { useEffect, useState } from 'react'
import { RefreshCw } from 'lucide-react'

declare const __BUILD_ID__: string

/**
 * A single-page app keeps running whatever JavaScript it loaded — a tab left
 * open all day never picks up a new deploy on its own. This polls the served
 * index.html for the current bundle hash and, when it changes, offers a reload.
 * Ends the "you're testing against stale code" trap for good.
 */
export default function UpdateGate() {
  const [stale, setStale] = useState(false)

  useEffect(() => {
    // The bundle hash THIS running app booted from.
    const loaded = Array.from(document.querySelectorAll('script[src]'))
      .map(s => (s as HTMLScriptElement).src)
      .find(src => /assets\/index-[A-Za-z0-9_-]+\.js/.test(src)) ?? ''
    const loadedHash = loaded.match(/index-([A-Za-z0-9_-]+)\.js/)?.[1] ?? ''
    if (!loadedHash) return

    let cancelled = false
    const check = async () => {
      try {
        const html = await fetch('/', { cache: 'no-store' }).then(r => r.text())
        const latest = html.match(/index-([A-Za-z0-9_-]+)\.js/)?.[1] ?? ''
        if (latest && latest !== loadedHash && !cancelled) setStale(true)
      } catch { /* offline / transient — try again next tick */ }
    }
    const id = setInterval(check, 60_000)
    check()
    return () => { cancelled = true; clearInterval(id) }
  }, [])

  if (!stale) return null
  return (
    <button
      onClick={() => window.location.reload()}
      className="fixed bottom-4 right-4 z-50 flex items-center gap-2 rounded-lg bg-brand-600 text-white px-4 py-2.5 text-sm font-medium shadow-lg hover:bg-brand-700"
    >
      <RefreshCw className="w-4 h-4" />
      A new version is available — click to reload
    </button>
  )
}

export const BUILD_ID = __BUILD_ID__
